import { FormEvent, useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import type { FolhaServicoDto } from "@/services/folhaServicos";

interface ModalServicoProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { nome: string; valorPadrao: number }) => Promise<void>;
  servico?: FolhaServicoDto | null;
}

export function ModalServico({ open, onClose, onSave, servico = null }: ModalServicoProps) {
  const [nome, setNome] = useState("");
  const [valorPadrao, setValorPadrao] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const editing = Boolean(servico);

  useEffect(() => {
    if (!open) return;
    setNome(servico?.nome ?? "");
    setValorPadrao(servico != null ? String(servico.valorUnitario) : "");
    setError("");
    setSaving(false);
  }, [open, servico]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const valor = Number(valorPadrao);
    if (!nome.trim()) {
      setError("Informe o nome do serviço.");
      return;
    }
    if (!Number.isFinite(valor) || valor < 0) {
      setError("Informe um valor padrão válido.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await onSave({ nome: nome.trim(), valorPadrao: valor });
      onClose();
    } catch {
      setError("Não foi possível salvar o serviço.");
    } finally {
      setSaving(false);
    }
  }

  function handleClose() {
    if (!saving) onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={editing ? "Editar serviço" : "Cadastrar serviço"}
      size="sm"
      closeOnEscape
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" form="folha-servico-form" loading={saving}>
            {editing ? "Salvar alterações" : "Cadastrar serviço"}
          </Button>
        </div>
      }
    >
      <form id="folha-servico-form" className="space-y-4" onSubmit={(e) => void handleSubmit(e)}>
        {editing && servico && (
          <p className="break-all text-xs text-slate-500">
            ID: <span className="font-mono">{servico.id}</span>
          </p>
        )}
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {error}
          </div>
        )}
        <Input
          label="Nome do Serviço"
          value={nome}
          onChange={(event) => {
            setNome(event.target.value);
            setError("");
          }}
          maxLength={160}
          required
          autoFocus
        />
        <Input
          label="Valor Padrão"
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          value={valorPadrao}
          onChange={(event) => {
            setValorPadrao(event.target.value);
            setError("");
          }}
          required
        />
      </form>
    </Modal>
  );
}
